/**
 * The browser's permission for system notifications (I-2915) — asked on a
 * user's gesture only, never on load: when the user picks „Glocke +
 * Systemhinweis" for the browser in the settings list
 * (`askForPermissionOnPick`, the settings list's own layers), or from the
 * kit toast the shell offers when a notification should pop up but the
 * browser was never asked (`systemNotifications.ts`).
 */
import { SHELL_DEVICE_TYPE } from '@/notificationDeviceSettings';

/** The browser's permission, or `unsupported` where it has no Notification API. */
export type SystemNotificationPermission = NotificationPermission | 'unsupported';

/** The browser's Notification API; null where it has none. */
export function notificationApi(): typeof Notification | null {
  return typeof globalThis.Notification === 'function' ? globalThis.Notification : null;
}

/** The browser's current permission for system notifications. */
export function systemNotificationPermission(): SystemNotificationPermission {
  return notificationApi()?.permission ?? 'unsupported';
}

/**
 * Asks the browser for the permission — call it only from a user's gesture
 * (a click), the browser ignores or penalizes a request without one. Asks
 * nothing when the browser decided already.
 */
export async function requestSystemNotificationPermission(): Promise<SystemNotificationPermission> {
  const api = notificationApi();
  if (!api) return 'unsupported';
  if (api.permission !== 'default') return api.permission;
  try {
    return await api.requestPermission();
  } catch {
    return api.permission;
  }
}

/**
 * Whether a write of the settings list picks „Glocke + Systemhinweis" for
 * the browser: `shell` → `notifications.devices.browser.modules.<module>` or
 * `….types.<module>.<name>` set to `system`.
 */
export function picksBrowserSystem(key: string, path: readonly string[], value: unknown): boolean {
  return (
    key === 'shell' &&
    value === 'system' &&
    path[0] === 'notifications' &&
    path[1] === 'devices' &&
    path[2] === SHELL_DEVICE_TYPE &&
    (path[3] === 'modules' || path[3] === 'types')
  );
}

/**
 * The settings list's hook for the user's own layers: picking „Glocke +
 * Systemhinweis" for the browser asks the browser's permission right away —
 * the pick is the gesture. Returns the request when one was made.
 */
export function askForPermissionOnPick(
  key: string,
  path: readonly string[],
  value: unknown
): Promise<SystemNotificationPermission> | null {
  if (!picksBrowserSystem(key, path, value) || systemNotificationPermission() !== 'default')
    return null;
  return requestSystemNotificationPermission();
}
